
import { useState, useEffect, memo } from 'react';
import { Grid3x3, X, ChevronLeft, ChevronRight, Share } from 'lucide-react';
import { Dialog, DialogContent, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import LazyImage from '@/components/ui/LazyImage';

interface PropertyGalleryProps {
  images: string[];
  title: string;
}

const PropertyGallery = memo(({
  images,
  title
}: PropertyGalleryProps) => {
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set([0])); // Preload first image
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const openGalleryModal = (index: number) => {
    setSelectedImageIndex(index);
    setIsGalleryModalOpen(true);
    // Preload current and adjacent images when modal opens
    const imagesToLoad = [index - 1, index, index + 1].filter(i => i >= 0 && i < images.length);
    setLoadedImages(prev => new Set([...prev, ...imagesToLoad]));
  };

  const goToPrevious = () => {
    const newIndex = selectedImageIndex === 0 ? images.length - 1 : selectedImageIndex - 1;
    setSelectedImageIndex(newIndex);
    // Preload adjacent image
    const prevIndex = newIndex === 0 ? images.length - 1 : newIndex - 1;
    setLoadedImages(prev => new Set([...prev, prevIndex]));
  };

  const goToNext = () => {
    const newIndex = selectedImageIndex === images.length - 1 ? 0 : selectedImageIndex + 1;
    setSelectedImageIndex(newIndex);
    // Preload adjacent image
    const nextIndex = newIndex === images.length - 1 ? 0 : newIndex + 1;
    setLoadedImages(prev => new Set([...prev, nextIndex]));
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast({
        description: "Se copió el enlace",
      });
    } catch (err) {
      console.error('Error al copiar URL:', err);
      toast({
        description: "Error al copiar el enlace",
        variant: "destructive",
      });
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isGalleryModalOpen) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goToPrevious();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goToNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isGalleryModalOpen, selectedImageIndex]);

  return <>
      <section className="mb-0 md:mb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 h-96 md:h-[500px] rounded-xl overflow-hidden border border-gray-200 shadow-sm">
              {/* Main large image - left side */}
              <div className="relative group cursor-pointer" onClick={() => openGalleryModal(0)}>
                <LazyImage
                  src={images[0]}
                  alt={title}
                  className="w-full h-full group-hover:brightness-110 transition-all duration-200"
                />
                
                {/* Botón de compartir simplificado en móvil */}
                {isMobile && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShare();
                    }}
                    className="absolute top-4 right-4 p-2 bg-white/80 hover:bg-white rounded-full shadow-sm backdrop-blur-sm"
                  >
                    <Share className="w-4 h-4 text-gray-700" />
                  </button>
                )}
              </div>
              
              {/* Right side grid with 4 smaller images */}
              <div className="hidden md:grid grid-cols-2 gap-2">
                {images.slice(1, 5).map((image, index) => (
                  <div key={index + 1} className="relative group cursor-pointer" onClick={() => openGalleryModal(index + 1)}>
                    <LazyImage
                      src={image}
                      alt={`${title} ${index + 2}`}
                      className="w-full h-full group-hover:brightness-110 transition-all duration-200"
                    />
                  </div>
                ))}
              </div>
            </div>
            
            {/* Show all photos button - positioned absolutely in bottom right */}
            <button onClick={() => openGalleryModal(0)} className="absolute bottom-4 right-4 flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 shadow-sm">
              <Grid3x3 className="w-4 h-4" />
              <span className="text-sm font-medium">Mostrar todas las fotos</span>
            </button>
          </div>
        </div>
      </section>

      {/* Gallery Modal */}
      <Dialog open={isGalleryModalOpen} onOpenChange={setIsGalleryModalOpen}>
        <DialogContent className="max-w-full max-h-full w-screen h-screen p-0 bg-black">
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Close button */}
            <DialogClose className="absolute top-4 left-4 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
              <X className="h-6 w-6" />
            </DialogClose>
            
            {/* Image counter */}
            <div className="absolute top-4 right-4 z-50 px-3 py-1 rounded-full bg-black/50 text-white text-sm">
              {selectedImageIndex + 1} / {images.length}
            </div>
            
            {/* Previous button */}
            <Button variant="outline" size="icon" onClick={goToPrevious} className="absolute left-4 top-1/2 -translate-y-1/2 z-50 bg-gray-500/80 hover:bg-gray-500 text-white border-gray-400 h-12 w-12">
              <ChevronLeft className="h-6 w-6" />
            </Button>
            
            {/* Next button */}
            <Button variant="outline" size="icon" onClick={goToNext} className="absolute right-4 top-1/2 -translate-y-1/2 z-50 bg-gray-500/80 hover:bg-gray-500 text-white border-gray-400 h-12 w-12">
              <ChevronRight className="h-6 w-6" />
            </Button>
            
            {/* Current image with conditional loading */}
            <LazyImage
              src={loadedImages.has(selectedImageIndex) ? images[selectedImageIndex] : images[0]}
              alt={`${title} ${selectedImageIndex + 1}`}
              className="max-w-full max-h-full object-contain"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>;
});

PropertyGallery.displayName = 'PropertyGallery';

export default PropertyGallery;
